import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { searchProductBySku } from '../../store/actions/inventory.action';
import BarcodeScannerComponent from '../common/BarCodeScanner';

/**
 * MobileSkuLookup
 * -----------------------------------------------------------------------------
 * Mobile-only, informational SKU lookup for the Order-Picking screen.
 *
 * Mirrors the "Scan / Enter SKU" behavior of the package-details Add-Item
 * popup (AddItemModal): scanning or typing a SKU fetches the product details
 * from the database via `searchProductBySku`. Unlike the pick-scan input,
 * this does NOT change any pick status — it is a read-only product card.
 *
 * A trigger button opens a full-screen overlay containing:
 *   - an auto-focused scan/enter input (hardware scanner or manual typing)
 *   - a camera scan button (html5-qrcode via BarcodeScannerComponent)
 *   - the fetched product detail card (image, name, SKU, size)
 */
/* istanbul ignore next */
const MobileSkuLookup = () => {
    const dispatch = useDispatch();
    const [show, setShow] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [skuInput, setSkuInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [product, setProduct] = useState(null);
    const [notFoundSku, setNotFoundSku] = useState('');
    const inputRef = useRef(null);
    const timerRef = useRef(null);
    const dialogRef = useRef(null);

    useEffect(() => {
        if (show && !showCamera) {
            setTimeout(() => inputRef.current?.focus(), 200);
        }
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [show, showCamera]);

    // While the overlay is open, keep the lookup input focused so hardware
    // scans route here — not to the pick-scan input that MobilePickPlan's
    // own focus loop keeps alive underneath. Skips when the user is on
    // another control inside the overlay (e.g. camera button).
    useEffect(() => {
        if (!show || showCamera) return undefined;
        const keepFocus = () => {
            const el = inputRef.current;
            const dialogEl = dialogRef.current;
            if (!el || !dialogEl) return;
            const active = document.activeElement;
            if (active === el) return;
            if (active && dialogEl.contains(active) && active !== document.body) return;
            el.focus();
        };
        keepFocus();
        const interval = setInterval(keepFocus, 400);
        return () => clearInterval(interval);
    }, [show, showCamera]);

    const buildSize = (p) => {
        if (p?.length && p?.width && p?.height) {
            const unit = p.dimensionUnit ? ` ${p.dimensionUnit}` : '';
            return `${p.length}x${p.width}x${p.height}${unit}`;
        }
        return '';
    };

    const lookupSku = async (rawValue) => {
        const scanned = String(rawValue || '').trim();
        if (!scanned || loading) return;

        setLoading(true);
        setNotFoundSku('');
        try {
            const data = {
                pageIndex: 1,
                pageSize: 30,
                sortColumn: '',
                sortOrder: 'DESCENDING',
                searchKeyword: scanned,
                sellerIdList: [],
                companyIdList: [],
            };
            // Promise.resolve keeps Sonar happy: dispatch's static type is not
            // thenable, but redux-thunk returns the service promise at runtime
            const response = await Promise.resolve(dispatch(searchProductBySku(data)));
            const list = response?.data?.Entity?.listInventoryEntity || [];
            const match =
                list.find((p) => String(p.productSku).toLowerCase() === scanned.toLowerCase()) ||
                list[0];
            if (match) {
                setProduct(match);
            } else {
                setProduct(null);
                setNotFoundSku(scanned);
            }
        } catch (err) {
            console.error('SKU lookup failed:', err);
            setProduct(null);
            setNotFoundSku(scanned);
        } finally {
            setLoading(false);
            setSkuInput('');
            inputRef.current?.focus();
        }
    };

    const handleClose = () => {
        setShow(false);
        setShowCamera(false);
        setSkuInput('');
        setProduct(null);
        setNotFoundSku('');
    };

    const imageUrl =
        product?.thumbnailUrl || product?.imageUrl || product?.productImage || '';

    return (
        <>
            <button
                type="button"
                className="mpp-action-btn msl-trigger-btn"
                onClick={() => setShow(true)}
                data-testid="msl-trigger"
            >
                <i className="fa fa-search" aria-hidden="true"></i> SKU Lookup
            </button>

            {show && (
                <dialog ref={dialogRef} className="msl-overlay" open>
                    <div className="msl-header">
                        <div className="msl-title">
                            <i className="fa fa-barcode" aria-hidden="true"></i> SKU Lookup
                        </div>
                        <button
                            type="button"
                            className="msl-close-btn"
                            onClick={handleClose}
                            aria-label="Close"
                        >
                            <i className="fa fa-times" aria-hidden="true"></i>
                        </button>
                    </div>

                    <div className="mpp-scan-wrapper">
                        <div className="mpp-scan-box">
                            <i className="fa fa-th mpp-scan-icon" aria-hidden="true"></i>
                            <div className="mpp-scan-content">
                                <div className="mpp-scan-label">Scan / Enter SKU</div>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    className="mpp-scan-input"
                                    value={skuInput}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setSkuInput(val);
                                        if (timerRef.current) clearTimeout(timerRef.current);
                                        if (val.trim()) {
                                            timerRef.current = setTimeout(() => lookupSku(val), 150);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (timerRef.current) clearTimeout(timerRef.current);
                                            lookupSku(skuInput);
                                        }
                                    }}
                                    placeholder="tap to type manually if needed"
                                    autoComplete="off"
                                    data-testid="msl-input"
                                />
                            </div>
                            <button
                                type="button"
                                className="mpp-scan-btn"
                                onClick={() => setShowCamera(true)}
                                title="Scan with camera"
                                data-testid="msl-camera-btn"
                            >
                                <i className="fa fa-camera" aria-hidden="true"></i>
                            </button>
                        </div>
                    </div>

                    {loading && (
                        <div className="msl-status">
                            <i className="fa fa-spinner fa-spin" aria-hidden="true"></i> Looking up
                            product...
                        </div>
                    )}

                    {!loading && notFoundSku && (
                        <div className="pick-scan-feedback pick-scan-error mpp-feedback">
                            <span className="pick-scan-icon">
                                <i className="fa fa-times-circle"></i>
                            </span>
                            <span className="pick-scan-message">
                                No product found for SKU "{notFoundSku}"
                            </span>
                        </div>
                    )}

                    {!loading && product && (
                        <div className="msl-card" data-testid="msl-product-card">
                            <div className="msl-card-img">
                                {imageUrl ? (
                                    <img src={imageUrl} alt={product.productName || product.productSku} />
                                ) : (
                                    <i className="fa fa-cube" aria-hidden="true"></i>
                                )}
                            </div>
                            <div className="msl-card-body">
                                <div className="msl-card-name">
                                    {product.productName || product.productSku}
                                </div>
                                <span className="mpp-sku-pill">{product.productSku}</span>
                                <div className="msl-card-rows">
                                    {buildSize(product) && (
                                        <div className="msl-card-row">
                                            <span className="msl-card-label">Size</span>
                                            <span>{buildSize(product)}</span>
                                        </div>
                                    )}
                                    {product.totalQuantity !== undefined &&
                                        product.totalQuantity !== null && (
                                            <div className="msl-card-row">
                                                <span className="msl-card-label">Total Qty</span>
                                                <span>{product.totalQuantity}</span>
                                            </div>
                                        )}
                                    {product.availableQuantity !== undefined &&
                                        product.availableQuantity !== null && (
                                            <div className="msl-card-row">
                                                <span className="msl-card-label">Available</span>
                                                <span>{product.availableQuantity}</span>
                                            </div>
                                        )}
                                </div>
                            </div>
                        </div>
                    )}

                    {!loading && !product && !notFoundSku && (
                        <div className="msl-empty-hint">
                            Scan a barcode or enter a SKU to view product details
                        </div>
                    )}

                    {showCamera && (
                        <BarcodeScannerComponent
                            showScanner={showCamera}
                            setShowScanner={setShowCamera}
                            handleScanSuccess={(decodedText) => {
                                setShowCamera(false);
                                lookupSku(decodedText);
                            }}
                        />
                    )}
                </dialog>
            )}
        </>
    );
};

export default MobileSkuLookup;
